import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./ErrorModal.css";
import { AlertCircle, X, UserPlus } from "lucide-react";

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  showRegisterButton?: boolean;
  onRegisterClick?: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = "Error",
  message,
  showRegisterButton = false,
  onRegisterClick,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      if (modalRef.current) modalRef.current.focus();
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="error-modal-overlay"
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
    >
      <div className="error-modal-content" ref={modalRef}>
        <button className="error-modal-close" onClick={onClose} title="Close">
          <X className="w-5 h-5" aria-label="Close modal" />
        </button>
        
        <div className="error-modal-icon">
          <AlertCircle className="w-20 h-20" />
        </div>
        
        <div className="error-modal-body">
          <h2 className="error-modal-title">{title}</h2>
          <p className="error-modal-message">{message}</p>
        </div>
        
        <div className="error-modal-footer">
          {showRegisterButton ? (
            <>
              <button 
                className="error-modal-button error-modal-register-button" 
                onClick={onRegisterClick || onClose}
              >
                <UserPlus size={18} />
                Create Account
              </button>
              <button 
                className="error-modal-button error-modal-cancel-button" 
                onClick={onClose}
              >
                Cancel
              </button>
            </>
          ) : (
            <button className="error-modal-button" onClick={onClose}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ErrorModal;


